import React from 'react';
import { motion } from 'framer-motion';
import { GingerAnimation, GarlicAnimation, PasteAnimation, BackgroundDecorations } from './GingerAnimation';
import { Star, Heart, Award, Zap, Leaf, CheckCircle } from 'lucide-react';

export const Hero: React.FC = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Decorations */}
      <BackgroundDecorations />
      
      {/* Animated Background Gradient */}
      <div className="absolute inset-0 hero-gradient opacity-10" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-block mb-6"
            >
              <div className="flex items-center space-x-2 bg-gradient-to-r from-ginger to-ginger-dark text-white px-6 py-3 rounded-full shadow-lg">
                <Zap className="w-5 h-5" />
                <span className="font-semibold">100% Homemade Premium Quality</span>
                <Leaf className="w-5 h-5" />
              </div>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Sai
              <span className="gradient-text block">Ginger Garlic</span>
              <span className="text-primary">Paste</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              Experience the authentic taste of our premium homemade ginger garlic paste. 
              Crafted with fresh ingredients, traditional methods, and lots of love. 
              Pure, natural, and bursting with flavor!
            </motion.p>

            {/* Feature Grid */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              {[
                { icon: Star, label: 'Premium Quality', color: 'text-yellow-500' },
                { icon: Heart, label: 'Made with Love', color: 'text-red-500' },
                { icon: Award, label: '100% Natural', color: 'text-green-500' },
                { icon: CheckCircle, label: 'Fresh Daily', color: 'text-blue-500' },
              ].map((feature, index) => (
                <motion.div
                  key={feature.label}
                  className="text-center group"
                  whileHover={{ scale: 1.1, y: -5 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
                >
                  <motion.div
                    className={`w-16 h-16 mx-auto mb-3 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-glow transition-all duration-300`}
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <feature.icon className={`w-8 h-8 ${feature.color}`} />
                  </motion.div>
                  <p className="text-sm font-semibold text-gray-700">{feature.label}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              <motion.a
                href="#order"
                className="btn-primary text-lg shadow-glow"
                whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(255, 107, 53, 0.5)" }}
                whileTap={{ scale: 0.95 }}
              >
                Order Now 🚀
              </motion.a>
              <motion.a
                href="#features"
                className="btn-secondary text-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More
              </motion.a>
            </motion.div>
          </motion.div>

          {/* Right Side - Animated Products */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative flex justify-center items-center"
          >
            {/* Central Paste Animation */}
            <div className="relative z-20">
              <motion.div
                className="text-center"
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <PasteAnimation />
                <motion.p
                  className="mt-6 font-bold text-xl gradient-text"
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Perfect Blend
                </motion.p>
              </motion.div>
            </div>

            {/* Side Ingredients */}
            <motion.div
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="text-center">
                <GingerAnimation />
                <motion.p
                  className="mt-4 font-bold text-lg text-ginger"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  Fresh Ginger
                </motion.p>
              </div>
            </motion.div>

            <motion.div
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2"
              animate={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="text-center">
                <GarlicAnimation />
                <motion.p
                  className="mt-4 font-bold text-lg text-ginger"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  Quality Garlic
                </motion.p>
              </div>
            </motion.div>

            {/* Floating Elements */}
            <motion.div
              className="absolute top-10 left-10 text-4xl opacity-30"
              animate={{ rotate: 360, scale: [1, 1.2, 1] }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            >
              🌿
            </motion.div>
            <motion.div
              className="absolute bottom-10 right-10 text-4xl opacity-30"
              animate={{ rotate: -360, scale: [1, 1.3, 1] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              ✨
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
